import React from "react";
import { cn } from "./utils";

const themeColor = (value) => {
  if (value === "background.paper") return "#fff";
  if (value === "error.light") return "#fee2e2";
  if (value === "error.main") return "#dc2626";
  if (value === "text.primary") return "#0f172a";
  if (value === "text.secondary") return "#64748b";
  if (value === "primary.main") return "#195790";
  if (value === "divider") return "#e2e8f0";
  return value;
};

const responsiveValue = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return value.lg ?? value.md ?? value.sm ?? value.xs;
};

const spacingValue = (value) => {
  const resolved = responsiveValue(value);
  return typeof resolved === "number" ? `${resolved * 8}px` : resolved;
};

const maxWidthValue = (value) => ({
  xs: "480px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
}[value] || value);

export const sxToStyle = (sx = {}) => {
  const style = {};

  Object.entries(sx || {}).forEach(([key, rawValue]) => {
    const value = responsiveValue(rawValue);
    if (typeof value === "function") return;
    if (key === "p") style.padding = spacingValue(value);
    else if (key === "pt") style.paddingTop = spacingValue(value);
    else if (key === "pb") style.paddingBottom = spacingValue(value);
    else if (key === "pl") style.paddingLeft = spacingValue(value);
    else if (key === "pr") style.paddingRight = spacingValue(value);
    else if (key === "px") {
      style.paddingLeft = spacingValue(value);
      style.paddingRight = spacingValue(value);
    } else if (key === "py") {
      style.paddingTop = spacingValue(value);
      style.paddingBottom = spacingValue(value);
    } else if (key === "mt") style.marginTop = spacingValue(value);
    else if (key === "mb") style.marginBottom = spacingValue(value);
    else if (key === "ml") style.marginLeft = spacingValue(value);
    else if (key === "mr") style.marginRight = spacingValue(value);
    else if (key === "mx") {
      style.marginLeft = spacingValue(value);
      style.marginRight = spacingValue(value);
    } else if (key === "my") {
      style.marginTop = spacingValue(value);
      style.marginBottom = spacingValue(value);
    } else if (key === "bgcolor" || key === "backgroundColor") style.backgroundColor = themeColor(value);
    else if (key === "color") style.color = themeColor(value);
    else if (key === "borderColor") style.borderColor = themeColor(value);
    else if (key === "maxWidth") style.maxWidth = maxWidthValue(value);
    else if (key === "borderRadius" && typeof value === "number") style.borderRadius = `${value * 8}px`;
    else if (!String(key).startsWith("&") && !String(key).startsWith("@")) style[key] = value;
  });

  return style;
};

export const Box = ({ alignItems, children, component, display, height, justifyContent, mb, ml, mr, mt, mx, my, p, px, py, sx, style, textAlign, width, ...props }) => {
  const Component = component || "div";
  return (
    <Component
      style={{
        ...(alignItems ? { alignItems } : {}),
        ...(display ? { display } : {}),
        ...(height ? { height } : {}),
        ...(justifyContent ? { justifyContent } : {}),
        ...(textAlign ? { textAlign } : {}),
        ...(width ? { width } : {}),
        ...(p !== undefined ? { padding: spacingValue(p) } : {}),
        ...(px !== undefined ? { paddingLeft: spacingValue(px), paddingRight: spacingValue(px) } : {}),
        ...(py !== undefined ? { paddingTop: spacingValue(py), paddingBottom: spacingValue(py) } : {}),
        ...(mt !== undefined ? { marginTop: spacingValue(mt) } : {}),
        ...(mb !== undefined ? { marginBottom: spacingValue(mb) } : {}),
        ...(ml !== undefined ? { marginLeft: spacingValue(ml) } : {}),
        ...(mr !== undefined ? { marginRight: spacingValue(mr) } : {}),
        ...(mx !== undefined ? { marginLeft: spacingValue(mx), marginRight: spacingValue(mx) } : {}),
        ...(my !== undefined ? { marginTop: spacingValue(my), marginBottom: spacingValue(my) } : {}),
        ...sxToStyle(sx),
        ...(style || {}),
      }}
      {...props}
    >
      {children}
    </Component>
  );
};

export const Container = ({ children, className, maxWidth = "xl", sx, style, ...props }) => {
  const widthClass = {
    sm: "max-w-3xl",
    md: "max-w-4xl",
    lg: "max-w-6xl",
    xl: "max-w-7xl"
  }[maxWidth] || "max-w-7xl";
  return (
    <div className={cn("mx-auto w-full px-4 sm:px-6 lg:px-8", widthClass, className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {children}
    </div>
  );
};

export const Paper = ({ children, className, elevation, sx, style, ...props }) => (
  <div
    className={cn("ui-paper rounded-md bg-white", elevation ? "shadow" : "", className)}
    style={{ ...sxToStyle(sx), ...(style || {}) }}
    {...props}
  >
    {children}
  </div>
);

const gridSpanClasses = {
  xs: {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
    5: "col-span-5",
    6: "col-span-6",
    7: "col-span-7",
    8: "col-span-8",
    9: "col-span-9",
    10: "col-span-10",
    11: "col-span-11",
    12: "col-span-12"
  },
  sm: {
    1: "sm:col-span-1",
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    7: "sm:col-span-7",
    8: "sm:col-span-8",
    9: "sm:col-span-9",
    10: "sm:col-span-10",
    11: "sm:col-span-11",
    12: "sm:col-span-12"
  },
  md: {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    5: "md:col-span-5",
    6: "md:col-span-6",
    7: "md:col-span-7",
    8: "md:col-span-8",
    9: "md:col-span-9",
    10: "md:col-span-10",
    11: "md:col-span-11",
    12: "md:col-span-12"
  },
  lg: {
    1: "lg:col-span-1",
    2: "lg:col-span-2",
    3: "lg:col-span-3",
    4: "lg:col-span-4",
    5: "lg:col-span-5",
    6: "lg:col-span-6",
    7: "lg:col-span-7",
    8: "lg:col-span-8",
    9: "lg:col-span-9",
    10: "lg:col-span-10",
    11: "lg:col-span-11",
    12: "lg:col-span-12"
  },
  xl: {
    1: "xl:col-span-1",
    2: "xl:col-span-2",
    3: "xl:col-span-3",
    4: "xl:col-span-4",
    5: "xl:col-span-5",
    6: "xl:col-span-6",
    7: "xl:col-span-7",
    8: "xl:col-span-8",
    9: "xl:col-span-9",
    10: "xl:col-span-10",
    11: "xl:col-span-11",
    12: "xl:col-span-12"
  }
};

const getGridSpanClasses = ({ lg, md, sm, xl, xs }) => [
  xs || sm || md || lg || xl ? gridSpanClasses.xs[xs] || "col-span-12" : "col-span-1",
  sm ? gridSpanClasses.sm[sm] : "",
  md ? gridSpanClasses.md[md] : "",
  lg ? gridSpanClasses.lg[lg] : "",
  xl ? gridSpanClasses.xl[xl] : ""
];

export const Grid = ({ alignItems, children, className, container, item, justifyContent, lg, md, sm, spacing = 0, sx, style, xl, xs, ...props }) => {
  if (container) {
    const justifyClass = justifyContent === "space-between" ? "justify-between" : "";
    return (
      <div
        className={cn("grid grid-cols-12", justifyClass, className)}
        style={{
          gap: spacingValue(spacing),
          ...(alignItems ? { alignItems } : {}),
          ...(justifyContent ? { justifyContent } : {}),
          ...sxToStyle(sx),
          ...(style || {}),
        }}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div className={cn(item ? getGridSpanClasses({ lg, md, sm, xl, xs }) : "", "min-w-0", className)} style={{ ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {children}
    </div>
  );
};

export const Link = ({ children, component, href, sx, style, to, underline, ...props }) => {
  const Component = component || "a";
  const linkProps = component ? { to } : { href };

  return (
    <Component
      className="transition hover:underline"
      style={{ ...sxToStyle(sx), ...(style || {}) }}
      {...linkProps}
      {...props}
    >
      {children}
    </Component>
  );
};

export const List = ({ children, sx }) => (
  <ul className="mt-3 space-y-2" style={sxToStyle(sx)}>
    {children}
  </ul>
);

export const ListItem = ({ children, sx }) => (
  <li style={sxToStyle(sx)}>
    {children}
  </li>
);

export const ListItemText = ({ primary }) => <>{primary}</>;

const typographyTags = {
  h1: "h1",
  h4: "h2",
  h5: "h3",
  h6: "h3",
  subtitle1: "h4",
  subtitle2: "h4",
  body1: "p",
  body2: "p",
  caption: "span",
};

export const Typography = ({ align, children, color, component, fontWeight, gutterBottom, sx, style, variant = "body1", ...props }) => {
  const Component = component || typographyTags[variant] || "p";
  return (
    <Component style={{ ...(align ? { textAlign: align } : {}), ...(color ? { color: themeColor(color) } : {}), ...(fontWeight ? { fontWeight } : {}), ...(gutterBottom ? { marginBottom: "0.35em" } : {}), ...sxToStyle(sx), ...(style || {}) }} {...props}>
      {children}
    </Component>
  );
};
